import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../lib/api';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// axios with credentials for cookie-based auth
const authClient = axios.create({ baseURL: API, withCredentials: true });

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [googleClientId, setGoogleClientId] = useState(null);
  const [isPhoneMock, setIsPhoneMock] = useState(true);

  const openAuthModal = () => setIsAuthModalOpen(true);
  const closeAuthModal = () => setIsAuthModalOpen(false);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await authClient.get('/auth/me');
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await authClient.get('/auth/me');
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // Fetch google auth configuration to get custom client ID
    authClient.get('/auth/config')
      .then(({ data }) => {
        if (data.google_client_id) {
          setGoogleClientId(data.google_client_id);
        }
        if (data.is_phone_mock !== undefined) {
          setIsPhoneMock(data.is_phone_mock);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Skip /me check if we're processing a session_id or OAuth code callback
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    if (hash.includes('session_id=') || search.includes('code=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (googleClientId) {
      const redirectUrl = window.location.origin + '/auth/callback';
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${encodeURIComponent(redirectUrl)}&response_type=code&scope=openid%20profile%20email`;
    } else {
      const redirectUrl = window.location.origin + '/';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  const login = async (email) => {
    const { data } = await authClient.post('/auth/login', { email });
    return data;
  };

  const signup = async (email, name) => {
    const { data } = await authClient.post('/auth/register', { email, name });
    return data;
  };

  const verifyEmail = async (email, code) => {
    const { data } = await authClient.post('/auth/verify-email', { email, code });
    setUser(data);
    return data;
  };

  const resendVerificationEmail = async (email) => {
    const { data } = await authClient.post('/auth/resend-verification', { email });
    return data;
  };

  const sendOtp = async (phone) => {
    const { data } = await authClient.post('/otp/send', { phone });
    return data;
  };

  const verifyOtp = async (phone, otp = null, name = null, token = null) => {
    const { data } = await authClient.post('/otp/verify', { phone, otp, name, token });
    setUser(data);
    return data;
  };

  const initMsg91Widget = (phone, onSuccess, onFailure) => {
    const phoneDigits = (phone || '').replace(/\D/g, '');
    const formattedPhone = phoneDigits.length === 10 ? `91${phoneDigits}` : phoneDigits;
    const widgetId = process.env.REACT_APP_MSG91_WIDGET_ID || "366843714a4e393639363037";
    const tokenAuth = process.env.REACT_APP_MSG91_TOKEN_AUTH || "565683Tt9zxMUQkvCu6a9318d1P1";
    
    if (typeof window.initSendOTP === 'function') {
      try {
        window.initSendOTP({
          widgetId,
          tokenAuth,
          identifier: formattedPhone,
          exposeMethods: true,
          success: (data) => {
            console.log('MSG91 Widget success:', data);
            if (onSuccess) onSuccess(data);
          },
          failure: (error) => {
            console.error('MSG91 Widget failure:', error);
            if (onFailure) onFailure(error);
          }
        });
        return true;
      } catch (err) {
        console.error('Failed to init MSG91 widget:', err);
      }
    }
    return false;
  };

  const logout = async () => {
    try { await authClient.post('/auth/logout'); } catch {}
    setUser(null);
  };

  const exchangeSession = async (session_id, code = null) => {
    const payload = session_id ? { session_id } : { code, redirect_uri: window.location.origin + '/auth/callback' };
    const { data } = await authClient.post('/auth/session', payload);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthModalOpen,
        openAuthModal,
        closeAuthModal,
        loginWithGoogle,
        login,
        signup,
        verifyEmail,
        resendVerificationEmail,
        sendOtp,
        verifyOtp,
        initMsg91Widget,
        logout,
        exchangeSession,
        authClient,
        refreshUser,
        isPhoneMock,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
